import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  CardActions,
  InputAdornment,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Home as HomeIcon,
  LocationOn as LocationIcon,
  Euro as EuroIcon,
  People as PeopleIcon,
  AccountBalance as BankIcon,
  Refresh as RefreshIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  buildFitColgroup,
  fitTruncateSx,
  fitHeaderLabel,
} from '../utils/fitTable';

interface Loan {
  id: number;
  loan_name: string;
  lender: string;
  current_balance: number;
}

interface Property {
  id: number;
  address: string;
  nickname: string;
  valuation: number;
  rental_income_yearly: number;
  lender: string;
  secodwom_uk_ltd_ownership: number;
  omhe_props_ltd_ownership: number;
  omar_ownership: number;
  heidi_ownership: number;
  sean_ownership: number;
  coral_ownership: number;
  dwayne_ownership: number;
  lena_ownership: number;
  airbnb_ical_url?: string | null;
  vrbo_ical_url?: string | null;
  created_at: string;
  updated_at: string;
  loans?: Loan[];
}

export const Properties: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'tile'>('table');
  const [selectedProperties, setSelectedProperties] = useState<number[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [nicknameFilter, setNicknameFilter] = useState<string>('');
  const [addressFilter, setAddressFilter] = useState<string>('');
  const [lenderFilter, setLenderFilter] = useState<string>('');
  
  const [formData, setFormData] = useState({
    address: '',
    nickname: '',
    valuation: '',
    rental_income_yearly: '',
    lender: '',
    secodwom_uk_ltd_ownership: '',
    omhe_props_ltd_ownership: '',
    omar_ownership: '',
    heidi_ownership: '',
    sean_ownership: '',
    dwayne_ownership: '',
    lena_ownership: '',
    airbnb_ical_url: '',
    vrbo_ical_url: ''
  });

  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/properties/');
      setProperties(response.data.properties || []);
    } catch (err: any) {
      console.error('Error fetching properties:', err);
      setError(err.response?.data?.error || 'Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  // Filter properties based on filter states
  const filteredProperties = properties.filter(property => {
    // Nickname filter (case-insensitive search)
    if (nicknameFilter && !property.nickname?.toLowerCase().includes(nicknameFilter.toLowerCase())) {
      return false;
    }
    
    // Address filter (case-insensitive search)
    if (addressFilter && !property.address?.toLowerCase().includes(addressFilter.toLowerCase())) {
      return false;
    }
    
    // Lender filter (case-insensitive search)
    if (lenderFilter && !property.lender?.toLowerCase().includes(lenderFilter.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  const handleOpenDialog = (property?: Property) => {
    if (property) {
      setSelectedProperty(property);
      setFormData({
        address: property.address,
        nickname: property.nickname,
        valuation: property.valuation.toString(),
        rental_income_yearly: property.rental_income_yearly.toString(),
        lender: property.lender || '',
        secodwom_uk_ltd_ownership: property.secodwom_uk_ltd_ownership.toString(),
        omhe_props_ltd_ownership: property.omhe_props_ltd_ownership.toString(),
        omar_ownership: property.omar_ownership.toString(),
        heidi_ownership: property.heidi_ownership.toString(),
        sean_ownership: property.sean_ownership.toString(),
        dwayne_ownership: property.dwayne_ownership.toString(),
        lena_ownership: property.lena_ownership.toString(),
        airbnb_ical_url: property.airbnb_ical_url || '',
        vrbo_ical_url: property.vrbo_ical_url || ''
      });
    } else {
      setSelectedProperty(null);
      setFormData({
        address: '',
        nickname: '',
        valuation: '',
        rental_income_yearly: '',
        lender: '',
        secodwom_uk_ltd_ownership: '',
        omhe_props_ltd_ownership: '',
        omar_ownership: '',
        heidi_ownership: '',
        sean_ownership: '',
        dwayne_ownership: '',
        lena_ownership: '',
        airbnb_ical_url: '',
        vrbo_ical_url: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedProperty(null);
    setError('');
  };

  const handleOpenDeleteDialog = (property: Property) => {
    setSelectedProperty(property);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedProperty(null);
  };

  const handleSubmit = async () => {
    setError('');
    try {
      const payload = {
        ...formData,
        valuation: parseFloat(formData.valuation) || 0,
        rental_income_yearly: parseFloat(formData.rental_income_yearly) || 0,
        secodwom_uk_ltd_ownership: parseFloat(formData.secodwom_uk_ltd_ownership) || 0,
        omhe_props_ltd_ownership: parseFloat(formData.omhe_props_ltd_ownership) || 0,
        omar_ownership: parseFloat(formData.omar_ownership) || 0,
        heidi_ownership: parseFloat(formData.heidi_ownership) || 0,
        sean_ownership: parseFloat(formData.sean_ownership) || 0,
        dwayne_ownership: parseFloat(formData.dwayne_ownership) || 0,
        lena_ownership: parseFloat(formData.lena_ownership) || 0
      };

      if (selectedProperty) {
        await api.put(`/api/properties/${selectedProperty.id}`, payload);
      } else {
        await api.post('/api/properties/', payload);
      }
      
      handleCloseDialog();
      fetchProperties();
    } catch (err: any) {
      console.error('Failed to save property:', err);
      setError(err.response?.data?.error || 'Failed to save property');
    }
  };

  const handleDelete = async () => {
    if (selectedProperty) {
      try {
        await api.delete(`/api/properties/${selectedProperty.id}`);
        handleCloseDeleteDialog();
        fetchProperties();
      } catch (err: any) {
        console.error('Failed to delete property:', err);
        setError(err.response?.data?.error || 'Failed to delete property');
      }
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedProperties(properties.map(p => p.id));
    } else {
      setSelectedProperties([]);
    }
  };

  const handleSelectOne = (propertyId: number) => {
    setSelectedProperties(prev => {
      if (prev.includes(propertyId)) {
        return prev.filter(id => id !== propertyId);
      } else {
        return [...prev, propertyId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedProperties.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedProperties.length} selected properties?`)) {
      return;
    }

    try {
      setError('');
      const deletePromises = selectedProperties.map(propertyId => 
        api.delete(`/api/properties/${propertyId}`)
      );
      
      await Promise.all(deletePromises);
      alert(`Successfully deleted ${selectedProperties.length} properties`);
      setSelectedProperties([]);
      fetchProperties();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete some properties');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculateTotalOwnership = (property: Property) => {
    return property.secodwom_uk_ltd_ownership + property.omhe_props_ltd_ownership + 
           property.omar_ownership + property.heidi_ownership + property.sean_ownership + 
           property.dwayne_ownership + property.lena_ownership;
  };

  const getOwnershipColor = (total: number) => {
    if (total === 100) return 'success';
    if (total > 100) return 'error';
    return 'warning';
  };

  const calculateEquity = (property: Property) => {
    const totalLoanBalance = property.loans?.reduce((sum, loan) => sum + loan.current_balance, 0) || 0;
    return property.valuation - totalLoanBalance;
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Properties 🏠
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              size="small"
              color="primary"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            {selectedProperties.length > 0 && viewMode === 'table' && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
              >
                Delete Selected ({selectedProperties.length})
              </Button>
            )}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="table">
                <ViewListIcon />
              </ToggleButton>
              <ToggleButton value="tile">
                <ViewModuleIcon />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchProperties}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Property
            </Button>
            <Button
              variant="outlined"
              component="label"
              startIcon={<HomeIcon />}
            >
              Import Properties
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    console.log('Properties import file selected:', file.name);
                    
                    try {
                      setLoading(true);
                      setError('');
                      
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      const response = await api.post('/api/properties/import', formData, {
                        headers: {
                          'Content-Type': 'multipart/form-data',
                        },
                      });
                      
                      console.log('Import successful:', response.data);
                      
                      // Refresh the properties list
                      await fetchProperties();
                      
                      // Show success message
                      alert(`Successfully imported properties from ${file.name}`);
                      
                    } catch (err: any) {
                      console.error('Import failed:', err);
                      setError(err.response?.data?.error || 'Failed to import properties');
                      alert(`Import failed: ${err.response?.data?.error || 'Unknown error'}`);
                    } finally {
                      setLoading(false);
                      // Reset file input
                      e.target.value = '';
                    }
                  }
                }}
              />
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Active Filters Chips */}
        {(nicknameFilter || addressFilter || lenderFilter) && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Active filters:
            </Typography>
            {nicknameFilter && (
              <Chip
                label={`Nickname: ${nicknameFilter}`}
                onDelete={() => setNicknameFilter('')}
                deleteIcon={<CloseIcon />}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {addressFilter && (
              <Chip
                label={`Address: ${addressFilter}`}
                onDelete={() => setAddressFilter('')}
                deleteIcon={<CloseIcon />}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {lenderFilter && (
              <Chip
                label={`Lender: ${lenderFilter}`}
                onDelete={() => setLenderFilter('')}
                deleteIcon={<CloseIcon />}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
              Showing {filteredProperties.length} of {properties.length} properties
            </Typography>
          </Box>
        )}

        {/* Filters Section */}
        <Collapse in={showFilters}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search Nickname"
                    placeholder="Property nickname..."
                    value={nicknameFilter}
                    onChange={(e) => setNicknameFilter(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search Address"
                    placeholder="Property address..."
                    value={addressFilter}
                    onChange={(e) => setAddressFilter(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search Lender"
                    placeholder="Lender name..."
                    value={lenderFilter}
                    onChange={(e) => setLenderFilter(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              {(nicknameFilter || addressFilter || lenderFilter) && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      setNicknameFilter('');
                      setAddressFilter('');
                      setLenderFilter('');
                    }}
                  >
                    Clear All Filters
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Collapse>

        {properties.length === 0 ? (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <HomeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No properties yet.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add your first property to get started.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : viewMode === 'table' ? (
              <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={FIT_TABLE_SX}>
                  <FitColgroup
                    cols={buildFitColgroup([
                      { key: 'select', pct: 3 },
                      { key: 'property', pct: 9 },
                      { key: 'address' },
                      { key: 'rental', pct: 7 },
                      { key: 'valuation', pct: 6.5 },
                      { key: 'lender', pct: 9 },
                      { key: 'equity', pct: 6.5 },
                      { key: 'secodwom', pct: 4.25 },
                      { key: 'omhe', pct: 4.25 },
                      { key: 'omar', pct: 4.25 },
                      { key: 'heidi', pct: 4.25 },
                      { key: 'sean', pct: 4.25 },
                      { key: 'coral', pct: 4.25 },
                      { key: 'dwayne', pct: 4.25 },
                      { key: 'lena', pct: 4.25 },
                      { key: 'actions', pct: 6 },
                    ])}
                  />
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedProperties.length === filteredProperties.length && filteredProperties.length > 0}
                          indeterminate={selectedProperties.length > 0 && selectedProperties.length < filteredProperties.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProperties(filteredProperties.map(p => p.id));
                            } else {
                              setSelectedProperties([]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>Property</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell align="right">{fitHeaderLabel('Rental\nIncome')}</TableCell>
                      <TableCell align="right">Valuation</TableCell>
                      <TableCell>Lender</TableCell>
                      <TableCell align="right">Equity</TableCell>
                      <TableCell align="center">{fitHeaderLabel('SECODWOM\nUK LTD')}</TableCell>
                      <TableCell align="center">{fitHeaderLabel('OmHe\nProps Ltd')}</TableCell>
                      <TableCell align="center">Omar</TableCell>
                      <TableCell align="center">Heidi</TableCell>
                      <TableCell align="center">Sean</TableCell>
                      <TableCell align="center">Coral</TableCell>
                      <TableCell align="center">Dwayne</TableCell>
                      <TableCell align="center">Lena</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredProperties.map((property) => {
                      const totalOwnership = calculateTotalOwnership(property);
                      return (
                        <TableRow 
                          key={property.id}
                          hover
                          selected={selectedProperties.includes(property.id)}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedProperties.includes(property.id)}
                              onChange={() => handleSelectOne(property.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                              <HomeIcon color="primary" fontSize="small" sx={{ flexShrink: 0 }} />
                              <Typography variant="body2" fontWeight="medium" sx={fitTruncateSx} title={property.nickname}>
                                {property.nickname}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                              <LocationIcon color="action" fontSize="small" sx={{ flexShrink: 0 }} />
                              <Typography variant="body2" sx={fitTruncateSx} title={property.address}>
                                {property.address}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatCurrency(property.rental_income_yearly)}/year
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(property.valuation)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {property.loans && property.loans.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {property.loans.map((loan, index) => (
                                  <Box key={loan.id || index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                    <BankIcon color="primary" fontSize="small" sx={{ flexShrink: 0 }} />
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography variant="body2" fontWeight="medium" sx={fitTruncateSx} title={loan.lender}>
                                        {loan.lender}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={fitTruncateSx}>
                                        Balance: {formatCurrency(loan.current_balance)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No mortgage
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium" color="success.main">
                              {formatCurrency(calculateEquity(property))}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="medium">
                              {property.secodwom_uk_ltd_ownership.toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="medium">
                              {property.omhe_props_ltd_ownership.toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="medium">
                              {property.omar_ownership.toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="medium">
                              {property.heidi_ownership.toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="medium">
                              {property.sean_ownership.toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="medium">
                              {property.coral_ownership.toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="medium">
                              {property.dwayne_ownership.toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="medium">
                              {property.lena_ownership.toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center" className="fit-table-no-clip">
                            <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                              <Tooltip title="Edit Property">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDialog(property)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Property">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDeleteDialog(property)}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {filteredProperties.map((property) => {
                  const totalOwnership = calculateTotalOwnership(property);
                  return (
                    <Box sx={{ flex: '1 1 calc(33.333% - 24px)', minWidth: '300px' }} key={property.id}>
                      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="h6" component="h2" gutterBottom>
                                {property.nickname}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                <LocationIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                {property.address}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Financial Details:
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Valuation</Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  €{property.valuation.toLocaleString()}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Rental Income</Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  €{property.rental_income_yearly.toLocaleString()}/yr
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Equity</Typography>
                                <Typography variant="body2" fontWeight="medium" color="success.main">
                                  €{calculateEquity(property).toLocaleString()}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Mortgage:
                            </Typography>
                            {property.loans && property.loans.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {property.loans.map((loan, index) => (
                                  <Box key={loan.id || index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BankIcon color="primary" fontSize="small" />
                                    <Box>
                                      <Typography variant="body2" fontWeight="medium">
                                        {loan.lender}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Balance: {formatCurrency(loan.current_balance)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No mortgage
                              </Typography>
                            )}
                          </Box>

                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Ownership ({(totalOwnership * 100).toFixed(1)}%):
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {property.secodwom_uk_ltd_ownership > 0 && (
                                <Chip label={`SECODWOM: ${property.secodwom_uk_ltd_ownership.toFixed(1)}%`} size="small" color="default" />
                              )}
                              {property.omhe_props_ltd_ownership > 0 && (
                                <Chip label={`OmHe Props: ${property.omhe_props_ltd_ownership.toFixed(1)}%`} size="small" color="default" />
                              )}
                              {property.omar_ownership > 0 && (
                                <Chip label={`Omar: ${property.omar_ownership.toFixed(1)}%`} size="small" color="primary" />
                              )}
                              {property.heidi_ownership > 0 && (
                                <Chip label={`Heidi: ${property.heidi_ownership.toFixed(1)}%`} size="small" color="secondary" />
                              )}
                              {property.sean_ownership > 0 && (
                                <Chip label={`Sean: ${property.sean_ownership.toFixed(1)}%`} size="small" color="success" />
                              )}
                              {property.dwayne_ownership > 0 && (
                                <Chip label={`Dwayne: ${property.dwayne_ownership.toFixed(1)}%`} size="small" color="info" />
                              )}
                              {property.lena_ownership > 0 && (
                                <Chip label={`Lena: ${property.lena_ownership.toFixed(1)}%`} size="small" color="warning" />
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenDialog(property)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleOpenDeleteDialog(property)}
                          >
                            Delete
                          </Button>
                        </CardActions>
                      </Card>
                    </Box>
                  );
                })}
              </Box>
        )}

        {/* Add/Edit Property Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedProperty ? 'Edit Property' : 'Add New Property'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Property Nickname"
                    value={formData.nickname}
                    onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                    required
                    placeholder="e.g., Dublin Apartment"
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    multiline
                    rows={2}
                    placeholder="Full property address"
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Airbnb iCal URL"
                    value={formData.airbnb_ical_url}
                    onChange={(e) => setFormData({ ...formData, airbnb_ical_url: e.target.value })}
                    placeholder="https://www.airbnb.ie/calendar/ical/...."
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="VRBO iCal URL"
                    value={formData.vrbo_ical_url}
                    onChange={(e) => setFormData({ ...formData, vrbo_ical_url: e.target.value })}
                    placeholder="https://www.vrbo.com/icalendar/...."
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Valuation"
                    type="number"
                    value={formData.valuation}
                    onChange={(e) => setFormData({ ...formData, valuation: e.target.value })}
                    inputProps={{ step: "1000" }}
                    required
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Annual Rental Income"
                    type="number"
                    value={formData.rental_income_yearly}
                    onChange={(e) => setFormData({ ...formData, rental_income_yearly: e.target.value })}
                    inputProps={{ step: "100" }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Lender"
                    value={formData.lender}
                    onChange={(e) => setFormData({ ...formData, lender: e.target.value })}
                    placeholder="Bank or lender name (optional)"
                  />
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Ownership Percentages
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="SECODWOM UK LTD (%)"
                    type="number"
                    value={formData.secodwom_uk_ltd_ownership}
                    onChange={(e) => setFormData({ ...formData, secodwom_uk_ltd_ownership: e.target.value })}
                    inputProps={{ step: "0.1", min: "0", max: "100" }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="OmHe Props Ltd (%)"
                    type="number"
                    value={formData.omhe_props_ltd_ownership}
                    onChange={(e) => setFormData({ ...formData, omhe_props_ltd_ownership: e.target.value })}
                    inputProps={{ step: "0.1", min: "0", max: "100" }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Omar Ownership (%)"
                    type="number"
                    value={formData.omar_ownership}
                    onChange={(e) => setFormData({ ...formData, omar_ownership: e.target.value })}
                    inputProps={{ step: "0.1", min: "0", max: "100" }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Heidi Ownership (%)"
                    type="number"
                    value={formData.heidi_ownership}
                    onChange={(e) => setFormData({ ...formData, heidi_ownership: e.target.value })}
                    inputProps={{ step: "0.1", min: "0", max: "100" }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Sean Ownership (%)"
                    type="number"
                    value={formData.sean_ownership}
                    onChange={(e) => setFormData({ ...formData, sean_ownership: e.target.value })}
                    inputProps={{ step: "0.1", min: "0", max: "100" }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Dwayne Ownership (%)"
                    type="number"
                    value={formData.dwayne_ownership}
                    onChange={(e) => setFormData({ ...formData, dwayne_ownership: e.target.value })}
                    inputProps={{ step: "0.1", min: "0", max: "100" }}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Lena Ownership (%)"
                    type="number"
                    value={formData.lena_ownership}
                    onChange={(e) => setFormData({ ...formData, lena_ownership: e.target.value })}
                    inputProps={{ step: "0.1", min: "0", max: "100" }}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!formData.nickname || !formData.address || !formData.valuation}
            >
              {selectedProperty ? 'Update' : 'Create'} Property
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Delete Property</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete this property? This action cannot be undone.
            </Typography>
            {selectedProperty && (
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2">Property Details:</Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {selectedProperty.nickname}
                </Typography>
                <Typography variant="body2">
                  <strong>Address:</strong> {selectedProperty.address}
                </Typography>
                <Typography variant="body2">
                  <strong>Valuation:</strong> {formatCurrency(selectedProperty.valuation)}
                </Typography>
                <Typography variant="body2">
                  <strong>Rental Income:</strong> {formatCurrency(selectedProperty.rental_income_yearly)}/year
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Delete Property
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};